import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAppState } from '../hooks/useAppState';
import { Deck, StudySettings } from '../types';
import { StudyMode } from '../components/StudyMode';
import { MatchGame } from '../components/MatchGame';
import { DeckList } from '../components/DeckList';
import { Button } from '../components/ui/Button';
import { Select } from '../components/ui/Select';
import { Switch } from '../components/ui/Switch';
import { Dialog } from '../components/ui/Dialog';
import {
  GraduationCap,
  Settings,
  ArrowLeft,
  BookOpen,
  ChevronRight,
  Timer,
  Gamepad2,
} from 'lucide-react';

export const StudyPage: React.FC = () => {
  const { decks, addSession } = useAppState();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const deckId = searchParams.get('deck');

  const [selectedDeck, setSelectedDeck] = useState<Deck | null>(null);
  const [showSettings, setShowSettings] = useState(false);
  const [gameMode, setGameMode] = useState<'study' | 'match'>('study');
  const [settings, setSettings] = useState<Partial<StudySettings>>({
    shuffle: true,
    showHints: true,
    autoAdvance: false,
    trackProgress: true,
    studyNewCards: true,
    studyReviewCards: true,
    cardsPerSession: 20,
    autoFlipEnabled: false,
    autoFlipDelay: 5,
    autoProgressEnabled: false,
    autoProgressDelay: 10,
  });

  useEffect(() => {
    if (deckId) {
      const deck = decks.find((d) => d.id === deckId);
      if (deck) {
        setSelectedDeck(deck);
      } else if (decks.length > 0) {
        setSelectedDeck(decks[0]);
      }
    } else if (decks.length > 0 && !selectedDeck) {
      setSelectedDeck(decks[0]);
    }
  }, [deckId, decks]);

  const handleComplete = () => {
    setSelectedDeck(null);
  };

  if (!selectedDeck) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-2">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            Study
          </h1>
        </div>
        <div className="text-center py-16">
          <div className="w-16 h-16 mx-auto bg-blue-50 dark:bg-blue-900/30 rounded-full flex items-center justify-center mb-4">
            <BookOpen className="w-8 h-8 text-blue-600 dark:text-blue-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
            Choose a deck to study
          </h3>
          <p className="text-gray-500 dark:text-gray-400 mb-6">
            Select one of your decks to start a study session.
          </p>
        </div>
        {decks.length > 0 && (
          <DeckList
            decks={decks}
            onSelectDeck={(deck) => setSelectedDeck(deck)}
            onStudyDeck={(deck) => setSelectedDeck(deck)}
          />
        )}
      </div>
    );
  }

  if (selectedDeck.cards.length === 0) {
    return (
      <div className="text-center py-16">
        <BookOpen className="w-16 h-16 text-gray-300 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">Empty Deck</h3>
        <p className="text-gray-500 dark:text-gray-400 mb-4">
          This deck has no cards. Add some cards first.
        </p>
        <Button onClick={() => navigate('/decks')}>Go to Decks</Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <button
            onClick={() => setSelectedDeck(null)}
            className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 mb-1"
          >
            <ArrowLeft className="w-4 h-4" /> Back to decks
          </button>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            {selectedDeck.name}
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {selectedDeck.cards.length} cards · {gameMode === 'study' ? 'Studying' : 'Match Game'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* Mode Toggle */}
          <div className="flex bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
            <button
              onClick={() => setGameMode('study')}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                gameMode === 'study'
                  ? 'bg-white dark:bg-gray-600 text-blue-600 dark:text-blue-400 shadow-sm'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
              }`}
            >
              Study
            </button>
            <button
              onClick={() => setGameMode('match')}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors flex items-center gap-1.5 ${
                gameMode === 'match'
                  ? 'bg-white dark:bg-gray-600 text-purple-600 dark:text-purple-400 shadow-sm'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
              }`}
            >
              <Gamepad2 className="w-4 h-4" />
              Match
            </button>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowSettings(!showSettings)}
            leftIcon={<Settings className="w-4 h-4" />}
          >
            Settings
          </Button>
        </div>
      </div>

      {/* Settings Panel */}
      {showSettings && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 space-y-4">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Study Settings</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Switch
              checked={settings.shuffle ?? true}
              onChange={(e) => setSettings({ ...settings, shuffle: !settings.shuffle })}
              label="Shuffle cards"
            />
            <Switch
              checked={settings.showHints ?? true}
              onChange={(e) => setSettings({ ...settings, showHints: !settings.showHints })}
              label="Show hints"
            />
            <Switch
              checked={settings.autoAdvance ?? false}
              onChange={(e) => setSettings({ ...settings, autoAdvance: !settings.autoAdvance })}
              label="Auto-advance"
            />
            <Switch
              checked={settings.trackProgress ?? true}
              onChange={(e) =>
                setSettings({ ...settings, trackProgress: !settings.trackProgress })
              }
              label="Track progress"
            />
            <Switch
              checked={settings.autoFlipEnabled ?? false}
              onChange={(e) => setSettings({ ...settings, autoFlipEnabled: !settings.autoFlipEnabled })}
              label="Auto-flip cards"
            />
            {settings.autoFlipEnabled && (
              <div className="col-span-1 md:col-span-2 pl-4 border-l-2 border-blue-200 dark:border-blue-800">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                  <Timer className="w-4 h-4 inline mr-1" />
                  Auto-flip delay (seconds)
                </label>
                <input
                  type="range"
                  min="2"
                  max="30"
                  step="1"
                  value={settings.autoFlipDelay ?? 5}
                  onChange={(e) =>
                    setSettings({ ...settings, autoFlipDelay: Number(e.target.value) })
                  }
                  className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer"
                />
                <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mt-1">
                  <span>2s</span>
                  <span className="font-medium">{settings.autoFlipDelay ?? 5}s</span>
                  <span>30s</span>
                </div>
              </div>
            )}
            <Switch
              checked={settings.autoProgressEnabled ?? false}
              onChange={(e) => setSettings({ ...settings, autoProgressEnabled: !settings.autoProgressEnabled })}
              label="Auto-progress cards"
            />
            {settings.autoProgressEnabled && (
              <div className="col-span-1 md:col-span-2 pl-4 border-l-2 border-green-200 dark:border-green-800">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                  <Timer className="w-4 h-4 inline mr-1" />
                  Auto-progress delay (seconds)
                </label>
                <input
                  type="range"
                  min="5"
                  max="60"
                  step="5"
                  value={settings.autoProgressDelay ?? 10}
                  onChange={(e) =>
                    setSettings({ ...settings, autoProgressDelay: Number(e.target.value) })
                  }
                  className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer"
                />
                <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mt-1">
                  <span>5s</span>
                  <span className="font-medium">{settings.autoProgressDelay ?? 10}s</span>
                  <span>60s</span>
                </div>
              </div>
            )}
            <div className="col-span-1 md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                Cards per session
              </label>
              <input
                type="range"
                min="5"
                max="50"
                step="5"
                value={settings.cardsPerSession ?? 20}
                onChange={(e) =>
                  setSettings({ ...settings, cardsPerSession: Number(e.target.value) })
                }
                className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer"
              />
              <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mt-1">
                <span>5</span>
                <span className="font-medium">{settings.cardsPerSession ?? 20}</span>
                <span>50</span>
              </div>
            </div>
          </div>
          <div className="flex justify-end">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowSettings(false)}
            >
              Close Settings
            </Button>
          </div>
        </div>
      )}

      {/* Study Mode / Match Game */}
      {gameMode === 'study' ? (
        <StudyMode
          cards={selectedDeck.cards}
          settings={settings}
          onComplete={(results) => {
            addSession({
              deckId: selectedDeck.id,
              cardIndex: 0,
              cards: selectedDeck.cards,
              startTime: new Date().toISOString(),
              results,
              settings: { ...settings } as StudySettings,
            });
            handleComplete();
          }}
          onExit={() => setSelectedDeck(null)}
        />
      ) : (
        <MatchGame
          cards={selectedDeck.cards}
          deckId={selectedDeck.id}
          deckName={selectedDeck.name}
          onComplete={(stats) => {
            console.log('Match game complete:', stats);
            handleComplete();
          }}
          onExit={() => setSelectedDeck(null)}
        />
      )}
    </div>
  );
};

export default StudyPage;
